import { TrashIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { Stack } from "@/core/components/ui/stack";
import { Textarea } from "@/core/components/ui/textarea";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  isOptimisticComment,
  useCreateAgentActionComment,
  useDeleteAgentActionComment,
} from "@/accounts-payable/query";
import type {
  ApAgentAction,
  ApAgentActionComment,
} from "@/accounts-payable/types";
import { formatApDateTime } from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";
import { SectionLabel, TextButton } from "./shared";

const CommentItem = ({
  comment,
  canDelete,
  onDelete,
  deleting,
}: {
  comment: ApAgentActionComment;
  canDelete: boolean;
  onDelete: () => void;
  deleting: boolean;
}) => (
  <Stack direction="row" gap="sm" align="start" justify="between" fullWidth>
    <Stack gap="none" className="min-w-0 flex-1">
      <Stack direction="row" gap="sm" align="baseline" className="flex-wrap">
        <span className="text-md font-semibold">
          {comment.author ?? "Unknown"}
        </span>
        <Typography variant="captionW1" textColor="tertiary">
          {formatApDateTime(comment.created_at)}
        </Typography>
      </Stack>
      <span className="whitespace-pre-wrap text-md text-secondary-text">
        {comment.content}
      </span>
    </Stack>
    {canDelete && (
      <button
        type="button"
        aria-label="Delete comment"
        onClick={onDelete}
        disabled={deleting}
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-tertiary-text transition-colors hover:bg-red-5 hover:text-red-70 disabled:opacity-50"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    )}
  </Stack>
);

/**
 * Reviewer comments thread + composer for a single agent action. The composer
 * rests as a one-line field (the height of the action pills) and grows to two
 * rows on focus; Cancel / Comment appear only once there's text, so a list of
 * actions isn't a stack of empty textareas. Escape leaves the field but keeps
 * the draft. Comments are
 * embedded on the action by the runs serializer, so we read `action.comments`;
 * the create/delete mutations optimistically update and refresh the runs cache.
 */
export const ActionComments = ({
  action,
  businessId,
}: {
  action: ApAgentAction;
  businessId: string | null;
}) => {
  const comments = action.comments ?? [];
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);

  const create = useCreateAgentActionComment(businessId);
  const remove = useDeleteAgentActionComment(businessId);

  const submit = () => {
    const content = draft.trim();
    if (!content) return;
    create.mutate(
      { actionId: action.id, content },
      {
        onSuccess: () => setDraft(""),
        onError: () => toast.error("Couldn't post comment. Please try again."),
      },
    );
  };

  const deleteComment = (commentId: string) =>
    remove.mutate(
      { actionId: action.id, commentId },
      {
        onError: () =>
          toast.error("Couldn't delete comment. Please try again."),
      },
    );

  const cancel = () => setDraft("");
  const hasDraft = draft.trim().length > 0;
  // Open while in use or holding unsent text; one line otherwise.
  const expanded = focused || draft.length > 0;

  return (
    <Stack gap="sm" fullWidth>
      {comments.length > 0 && (
        <Stack gap="sm" fullWidth>
          <SectionLabel>Comments ({comments.length})</SectionLabel>
          <Stack gap="md" fullWidth>
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                canDelete={
                  // Server decides ownership (can_delete); the optimistic guard
                  // just holds off until the comment has a real id to delete.
                  comment.can_delete && !isOptimisticComment(comment)
                }
                deleting={remove.isPending}
                onDelete={() => deleteComment(comment.id)}
              />
            ))}
          </Stack>
        </Stack>
      )}

      <Stack gap="xs" align="end" fullWidth>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          // Escape only leaves the field; the text stays until Cancel, so a
          // half-written note isn't lost to a stray key.
          onKeyDown={(e) => {
            if (e.key === "Escape") e.currentTarget.blur();
          }}
          placeholder="Add a comment…"
          aria-label="Comment"
          rows={expanded ? 2 : 1}
          // Sized down from core's 8rem / 15px form field to sit in a dense
          // action row: 32px at rest, the height of the xsPill buttons. The
          // half-step py-1.5 is deliberate: 16px line + 2px border + 12px
          // fits 32px; py-2 would overflow it.
          className={cn(
            "min-h-0 resize-none py-1.5 text-md placeholder:text-md",
            { "h-8": !expanded },
          )}
        />
        {hasDraft && (
          <Stack direction="row" gap="xs" align="center">
            <TextButton
              tone="secondary"
              onClick={cancel}
              disabled={create.isPending}
            >
              Cancel
            </TextButton>
            {/* The label carries the in-flight state, so there's a visible
                change while the comment posts. */}
            <TextButton
              onClick={submit}
              disabled={create.isPending}
              aria-busy={create.isPending || undefined}
            >
              {create.isPending ? "Posting…" : "Comment"}
            </TextButton>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
